// src/shared/services/ContactService.js
import { 
    collection, 
    getDocs, 
    doc, 
    updateDoc, 
    query, 
    orderBy, 
    where,
    Timestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseService } from './FirebaseService';
  
  class ContactServiceClass {
    constructor() {
      this.db = null;
      this.initialized = false;
    }
  
    /**
     * Initialize the service
     */
    async initialize() {
      if (this.initialized) return;
      
      try {
        await FirebaseService.initialize();
        this.db = FirebaseService.getFirestore();
        this.initialized = true;
        console.log('✅ ContactService initialized');
      } catch (error) {
        console.error('❌ ContactService initialization failed:', error);
        throw error;
      }
    }
  
    /**
     * Submit a contact form from the landing page
     */
    async submitContactForm(contactData) {
      try {
        console.log('🔄 ContactService: Initializing...');
        await this.initialize();
        console.log('✅ ContactService: Initialized successfully');
        
        console.log('📝 ContactService: Calling submitContactLead...');
        const submitContactLead = httpsCallable(getFunctions(), 'submitContactLead');
        const result = await submitContactLead({
          ...contactData,
          userAgent: navigator.userAgent
        });

        console.log('✅ Contact form submitted successfully with ID:', result.data?.id);
        return result.data?.id;
        
      } catch (error) {
        console.error('❌ ContactService Error Details:');
        console.error('- Error message:', error.message);
        console.error('- Error code:', error.code);
        console.error('- Full error:', error);
        
        // More specific error messages
        if (error.code === 'permission-denied') {
          throw new Error('Permission denied: Please check Firestore security rules');
        } else if (error.code === 'unavailable') {
          throw new Error('Contact service unavailable');
        } else if (error.message.includes('Firebase')) {
          throw new Error('Firebase connection error');
        } else {
          throw new Error(`Failed to submit contact form: ${error.message}`);
        }
      }
    }
  
    /**
     * Get all contact submissions (for admin)
     */
    async getAllSubmissions() {
      await this.initialize();
      
      try {
        const q = query(
          collection(this.db, 'contact_submissions'),
          orderBy('timestamp', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const submissions = [];
        
        querySnapshot.forEach((doc) => {
          submissions.push({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate()
          });
        });
        
        return submissions;
      } catch (error) {
        console.error('❌ Error fetching contact submissions:', error);
        throw new Error('Failed to fetch contact submissions');
      }
    }
  
    /**
     * Get new/unread submissions (for admin dashboard)
     */
    async getNewSubmissions() {
      await this.initialize();
      
      try {
        const q = query(
          collection(this.db, 'contact_submissions'),
          where('status', '==', 'new'),
          orderBy('timestamp', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const submissions = [];
        
        querySnapshot.forEach((doc) => {
          submissions.push({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate()
          });
        });
        
        return submissions;
      } catch (error) {
        console.error('❌ Error fetching new submissions:', error);
        throw new Error('Failed to fetch new submissions');
      }
    }
  
    /**
     * Mark submission as read/reviewed (for admin)
     */
    async markAsRead(submissionId) {
      await this.initialize();
      
      try {
        const submissionRef = doc(this.db, 'contact_submissions', submissionId);
        await updateDoc(submissionRef, {
          status: 'read',
          reviewed_at: Timestamp.fromDate(new Date())
        });
        
        console.log('✅ Submission marked as read:', submissionId);
      } catch (error) {
        console.error('❌ Error marking submission as read:', error);
        throw new Error('Failed to update submission status');
      }
    }
  
    /**
     * Mark submission as responded (for admin)
     */
    async markAsResponded(submissionId, response) {
      await this.initialize();
      
      try {
        const submissionRef = doc(this.db, 'contact_submissions', submissionId);
        await updateDoc(submissionRef, {
          status: 'responded',
          response: response,
          responded_at: Timestamp.fromDate(new Date())
        });
        
        console.log('✅ Submission marked as responded:', submissionId);
      } catch (error) {
        console.error('❌ Error marking submission as responded:', error);
        throw new Error('Failed to update submission response');
      }
    }
  
    /**
     * Get submission statistics (for admin dashboard)
     */
    async getSubmissionStats() {
      await this.initialize();
      
      try {
        const allSubmissions = await this.getAllSubmissions();
        
        const stats = {
          total: allSubmissions.length,
          new: allSubmissions.filter(s => s.status === 'new').length,
          read: allSubmissions.filter(s => s.status === 'read').length,
          responded: allSubmissions.filter(s => s.status === 'responded').length,
          today: allSubmissions.filter(s => {
            const today = new Date();
            const submissionDate = s.timestamp;
            return submissionDate && 
                   submissionDate.toDateString() === today.toDateString();
          }).length,
          thisWeek: allSubmissions.filter(s => {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return s.timestamp && s.timestamp >= weekAgo;
          }).length
        };
        
        return stats;
      } catch (error) {
        console.error('❌ Error getting submission stats:', error);
        throw new Error('Failed to get submission statistics');
      }
    }
  }
  
  // Export singleton instance
  export const ContactService = new ContactServiceClass();
